import React, { useState, useRef, useEffect } from "react";
import { Box, Button, TextField, Typography, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import AuthLayout from "../../components/AuthLayout";
import API_BASE_URL from "../../config/api";

export default function ForgotPassword() {
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: New Password
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(10 * 60); // 10 minutes
  const inputRefs = useRef([]);
  const navigate = useNavigate();

  // Countdown timer for OTP
  useEffect(() => {
    if (step === 2 && timeLeft > 0) {
      const timer = setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [step, timeLeft]);

  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  // Step 1: Send OTP to email
  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/users/forgot-password`, { email });
      toast.success("OTP sent to your email!");
      setStep(2);
      setTimeLeft(10 * 60); // Reset timer
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    const otpCode = otp.join("");
    if (otpCode.length !== 4) {
      toast.error("Please enter the 4-digit OTP");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/users/verify-reset-otp`, {
        email,
        otp: otpCode,
      });
      toast.success("OTP verified successfully!");
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/users/reset-password`, {
        email,
        otp: otp.join(""),
        newPassword,
      });
      toast.success("Password reset successfully!");
      navigate("/login");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  // OTP input handlers
  const handleOtpChange = (e, index) => {
    const value = e.target.value.replace(/\D/, "");
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 3) inputRefs.current[index + 1].focus();
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_BASE_URL}/api/users/forgot-password`, { email });
      toast.success("OTP resent to your email!");
      setTimeLeft(10 * 60);
      setOtp(["", "", "", ""]);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      {/* Step 1: Enter Email */}
      {step === 1 && (
        <Box
          component="form"
          onSubmit={handleSendOTP}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Typography
            textAlign="center"
            fontWeight={600}
            color="white"
            sx={{
              fontSize: {
                xs: "1.12rem",
                sm: "1.5rem",
                md: "1.75rem",
                lg: "2rem",
              },
            }}
          >
            Forgot Password
          </Typography>
          <Typography variant="body2" textAlign="center" color="white">
            Enter your email to receive a password reset code
          </Typography>

          <TextField
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
          />

          <Button variant="contained" type="submit" size="large" disabled={loading}>
            {loading ? "Sending..." : "Send OTP"}
          </Button>

          <Button
            variant="text"
            onClick={() => navigate("/login")}
            sx={{ color: "white" }}
          >
            Back to Login
          </Button>
        </Box>
      )}

      {/* Step 2: Verify OTP */}
      {step === 2 && (
        <Box
          component="form"
          onSubmit={handleVerifyOTP}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Typography
            textAlign="center"
            fontWeight={600}
            color="white"
            sx={{
              fontSize: {
                xs: "1.12rem",
                sm: "1.5rem",
                md: "1.75rem",
                lg: "2rem",
              },
            }}
          >
            Verify OTP
          </Typography>
          <Typography variant="body2" textAlign="center" color="white">
            Enter the 4-digit code sent to {email}
          </Typography>

          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 1.5 }}
            justifyContent="center"
            sx={{ mt: 1 }}
          >
            {otp.map((digit, index) => (
              <TextField
                key={index}
                inputRef={(el) => (inputRefs.current[index] = el)}
                value={digit}
                onChange={(e) => handleOtpChange(e, index)}
                onKeyDown={(e) => handleOtpKeyDown(e, index)}
                inputProps={{
                  maxLength: 1,
                  style: {
                    textAlign: "center",
                    fontSize: "1.2rem",
                    width: "2.3rem",
                    height: "2.6rem",
                  },
                }}
                variant="outlined"
              />
            ))}
          </Stack>

          <Typography variant="body2" textAlign="center" sx={{ color: "white", mt: 1 }}>
            Code expires in {formatTime(timeLeft)}
          </Typography>

          <Button variant="contained" type="submit" size="large" disabled={loading}>
            {loading ? "Verifying..." : "Verify OTP"}
          </Button>

          <Button
            variant="text"
            onClick={handleResendOTP}
            disabled={loading || timeLeft > 0}
            sx={{ color: "white" }}
          >
            {timeLeft > 0 ? "Resend OTP" : "Resend OTP"}
          </Button>
        </Box>
      )}

      {/* Step 3: Reset Password */}
      {step === 3 && (
        <Box
          component="form"
          onSubmit={handleResetPassword}
          sx={{ display: "flex", flexDirection: "column", gap: 2 }}
        >
          <Typography
            textAlign="center"
            fontWeight={600}
            color="white"
            sx={{
              fontSize: {
                xs: "1.12rem",
                sm: "1.5rem",
                md: "1.75rem",
                lg: "2rem",
              },
            }}
          >
            Reset Password
          </Typography>
          <Typography variant="body2" textAlign="center" color="white">
            Enter your new password
          </Typography>

          <TextField
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            fullWidth
          />

          <TextField
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            fullWidth
          />

          <Button variant="contained" type="submit" size="large" disabled={loading}>
            {loading ? "Resetting..." : "Reset Password"}
          </Button>
        </Box>
      )}
    </AuthLayout>
  );
}
